import { Component, OnInit, ViewEncapsulation, ViewChild, ElementRef, HostBinding } from '@angular/core';
import { HelpIndex, PasswordListType, Endpoint, PasswordSourceType, PasswordListOperationType } from '../../../models/enums';
import { GenericColDef, GridContext, SortByFilter, FilterTemplateType, AlignDirection, GridRowSelectionType, CellTemplateType } from '../../../models/grid-context';
import { RestService } from '../../../services/rest/rest.service';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { CommonService } from '../../../services/common/common.service';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { AddPasswordsModalComponent } from '../add-passwords-modal/add-passwords-modal.component';
import { Permissions, ObjectActionListType } from '../../../models/permission';
import { DeferredPromise } from '../../../models/deferred-promise';

@Component({
    standalone: false,
    selector: 'res-known-frequently-used-passwords-modal',
    templateUrl: './known-frequently-used-passwords-modal.component.html',
    styleUrls: ['./known-frequently-used-passwords-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class KnowFrequentlyUsedPasswordsModalComponent extends BaseModalComponent implements OnInit {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent) genericGrid: GenericGridComponent;
    @ViewChild('downloadLink', { read: ElementRef, static: true }) downloadLink: ElementRef;

    modalAutomationId = '';
    modalClass = 'known-passwords-modal'
    modalTitle = '';
    containerTitle = '';
    modalHelpIndex;
    gridName = '';
    requestInfo: DrRequestInfo;
    isBusy = false;
    isBusyMessage = '';
    numberOfSelectedPasswords = 0;
    hasDownloadPermission: boolean;
    needsUpdate;

    columnDefs: Array<GenericColDef> = new Array<GenericColDef>(
        {
            colId: 'checkbox', automationId: this.modalAutomationId,
            checkboxSelection: true, headerCheckboxSelection: true,
            minWidth: 36, maxWidth: 36, width: 36,
            suppressSizeToFit: true, suppressMovable: true
        },
        {
            headerName: 'Password',
            field: 'password',
            automationId: this.modalAutomationId,
            filterType: FilterTemplateType.freeform,
            serviceTierFilter: true,
            lockVisible: true,
            defaultSortBy: SortByFilter.NAME,
            width: 217,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            preserveSpaces: true
        },
        {
            headerName: 'Date Added', field: 'dateAdded', sortBy: SortByFilter.DATE_CREATED, automationId: this.modalAutomationId,
            headerAlignDirection: AlignDirection.right, cellAlignDirection: AlignDirection.right,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            width: 147,
            defaultSortAscending: false
        },
        {
            headerName: 'Source', field: 'source', automationId: this.modalAutomationId,
            serviceTierFilter: true,
            filterType: FilterTemplateType.singleChoice,
            filterItems: ['All', PasswordSourceType.FILE, PasswordSourceType.FOUND_PASSWORDS, PasswordSourceType.MANUAL],
            filterItemSelected: 'All',
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            width: 200
        }
    );

    gridContext = new GridContext({
        inlineGridFilter: true,
        sortAscending: true,
        sortAscendingFieldName: 'descending',
        sortByFieldName: 'sortByFilter',
        sortBy: SortByFilter.NAME,
        isFilterNameChangeable: true
    });

    rowSelection = GridRowSelectionType.multipleCheckboxWithRowClickSelection;

    constructor(private restService: RestService, private modalRef: BsModalRef, private modalService: BsModalService, private commonService: CommonService) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        this.hasDownloadPermission = Permissions.checkUserPermission(ObjectActionListType.DOCUMENT_REPORTS);
        if (this.modalData.modalType === PasswordListType.KNOWN_PASSWORDS) {
            this.modalAutomationId = 'known-passwords-modal';
            this.modalTitle = 'View / Edit Known Passwords';
            this.containerTitle = this.gridName = 'Known Passwords';
            this.modalHelpIndex = HelpIndex.password_view_edit_known_passwords_dlg;
        } else {
            this.modalAutomationId = 'frequently-used-passwords-modal';
            this.modalTitle = 'View / Edit Frequently Used Passwords';
            this.containerTitle = this.gridName = 'Frequently Used Passwords';
            this.modalHelpIndex = HelpIndex.password_view_edit_freqused_passwords_dlg;
            const sourceColumn = this.columnDefs.find(element => element.field === 'source');
            sourceColumn.filterItems = sourceColumn.filterItems.filter(item => item !== PasswordSourceType.FOUND_PASSWORDS);
        }

        this.requestInfo = new DrRequestInfo({
            data: {
                startIndex: 0,
                count: 100,
                filters: [],
                selectedAttributes: [],
                descending: false,
                passwordListType: this.modalData.modalType,
                projectId: this.modalData.handle,
                requestHandle: this.modalData.handle
            },
            endpoint: Endpoint.projectManager_getPasswordList,
            responseName: 'passwordData'
        });
        this.needsUpdate = false;
    }

    selectedItems(): string {
        if (this.genericGrid) {
            this.numberOfSelectedPasswords = this.genericGrid.selectedRows.length;
            if (this.numberOfSelectedPasswords > 0) {
                if (this.genericGrid.allRowsSelected) {
                    this.numberOfSelectedPasswords = this.genericGrid.totalItems;
                    return `All ${this.numberOfSelectedPasswords.toLocaleString()}`;
                }
                return `${this.numberOfSelectedPasswords.toLocaleString()} on this page`;
            }
            return `${this.numberOfSelectedPasswords.toLocaleString()}`;
        }
        this.numberOfSelectedPasswords = 0;
        return '0';
    }

    responseArrayCallback = (response: any): void => {
        if (response.dateAdded) {
            const datetime = response.dateAdded.split('-');
            if (datetime.length > 5) {
                response.dateAdded = this.commonService.insertYesterday(datetime[0] + '-' + datetime[1] + '-' + datetime[2] + ' ' + datetime[3] + ':' + datetime[4] + ':' + datetime[5]);
            }
        }
        if (response.source) {
            response.source = response.source.toLowerCase() === PasswordSourceType.MANUAL.toLowerCase() ?
                PasswordSourceType.MANUAL : response.source.toLowerCase() === PasswordSourceType.IMPORT.toLowerCase() ? PasswordSourceType.FOUND_PASSWORDS : response.source
        }
        return response;
    }

    responseCallback = (response: any) => {
        if (!response.passwordData) {
            this.genericGrid.isBusy = false;
        }
    }

    closeModal(result?: boolean, data?: any): void {
        this.modalRef.hide();
        this.onClose.resolve({ modalResult: true, modalData: this.needsUpdate });
    }

    resetAndReloadGrid(): void {
        this.requestInfo.data.startIndex = 0;
        this.requestInfo.data.filters = [];
        this.requestInfo.data.descending = false;
        if (this.requestInfo.data.sortByFilter) {
            delete this.requestInfo.data.sortByFilter;
        }
        this.genericGrid.isDataFiltered = false;
        this.genericGrid.gridCheckedItems = new Array<any>();
        this.genericGrid.gridContext.clearAllFilters.emit();
        this.genericGrid.gridContext.sortBy = SortByFilter.NAME;
        this.genericGrid.gridContext.sortAscending = true;
        this.genericGrid.loadData();
    }

    importPasswords() {
        this.commonService.openConfirmationInfoModal(
            'All Found Passwords will be imported. Duplicates will be skipped.',
            'Import Found Passwords', 'Continue', 'Cancel'
        ).then((result) => {
            if (result && result.modalResult) {
                this.restService.post(Endpoint.projectManager_importPasswordList, {
                    passwordListType: this.modalData.modalType,
                    passwords: [],
                    deleteAll: false,
                    passwordListOperationType: PasswordListOperationType.IMPORT,
                    projectId: this.modalData.handle
                }).then(response => {
                    if (response?.result) {
                        this.needsUpdate = true;
                        const message = `${response?.result.numAdded} unique Found Passwords were imported.\n${response?.result.numSkipped} duplicates were skipped.`;
                        this.commonService.openInformationToastNotification(message.replace(/\n/g, '<br/>'));
                        this.resetAndReloadGrid();
                    }
                });
            }
        });
    }

    deleteSelectedPasswords() {
        if (this.numberOfSelectedPasswords > 0) {
            this.commonService.openConfirmationDeleteModal(
                `Delete the ${this.numberOfSelectedPasswords.toLocaleString()} selected passwords?`,
                'Delete Passwords', 'Delete Passwords', 'Cancel'
            ).then((result) => {
                if (result && result.modalResult) {
                    const passwordsList = [];
                    const deletePromise = new DeferredPromise<any>();
                    if (this.requestInfo?.data?.filters?.length > 0 && this.genericGrid.allRowsSelected && this.numberOfSelectedPasswords > 100) {
                        const data = {
                            startIndex: -1,
                            count: 100,
                            filters: this.requestInfo.data.filters,
                            selectedAttributes: [],
                            descending: false,
                            passwordListType: this.modalData.modalType,
                            projectId: this.modalData.handle,
                            requestHandle: this.modalData.handle
                        };
                        this.restService.post(this.requestInfo.endpoint, data).then(response => {
                            if (response && response.passwordData) {
                                response.passwordData.forEach(pass => {
                                    passwordsList.push(pass.password)
                                });
                                deletePromise.resolve();
                            }
                        }, error => {
                            console.error('Failed to get password list without paging');
                        });

                    } else if ((this.requestInfo?.data?.filters?.length > 0 && this.genericGrid.allRowsSelected) || !this.genericGrid.allRowsSelected) {
                        const selectedPasswordsForDeletion = this.genericGrid.gridApi.getSelectedRows();
                        selectedPasswordsForDeletion.forEach(pass => {
                            passwordsList.push(pass.password)
                        });
                        deletePromise.resolve();
                    } else {
                        deletePromise.resolve();
                    }
                    deletePromise.promise.then(() => {
                        this.restService.post(Endpoint.projectManager_deleteFromPasswordList, {
                            passwordListType: this.modalData.modalType,
                            passwords: passwordsList,
                            deleteAll: passwordsList.length > 0 ? false : true,
                            passwordListOperationType: PasswordListOperationType.DELETE,
                            projectId: this.modalData.handle
                        }).then(response => {
                            this.needsUpdate = true;
                            this.resetAndReloadGrid();
                        });
                    })
                }
            });
        }
    }

    downloadPasswordList(): void {
        const endpoint = Endpoint.projectManager_downloadPasswordDataUrl;
        const postParams = {
            projectId: this.modalData.handle,
            passwordListType: this.modalData.modalType,
            filters: this.requestInfo.data.filters
        };
        this.restService.post(endpoint, postParams).then(response => {
            if (response) {
                this.commonService.unzipFile(response.viewManifestUrl, this.downloadLink.nativeElement, this.containerTitle.replace(/ /g, ''));
            }
        }, error => {
            console.error('Failed to download!');
        });
    }

    openAddPasswordModal(): void {
        this.commonService.openModal(AddPasswordsModalComponent, {
            modalData: {
                modalType: this.modalData.modalType,
                handle: this.modalData.handle
            },
            class: 'horizontal-px-500'
        }).then(response => {
            if (response?.modalData?.result) {
                this.needsUpdate = true;
                const message = `${response?.modalData.result.numAdded} unique passwords were added.\n${response?.modalData.result.numSkipped} duplicates were skipped.`;
                this.commonService.openInformationToastNotification(message.replace(/\n/g, '<br/>'));
                this.resetAndReloadGrid();
            }
        });
    }
}
